import * as React from 'react';
import dayjs from 'dayjs';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import useAxios from '../utils/useAxios';
import { EmptyState, NumberField, SectionCard, StatCard, currency } from './ui';
import { errorText, shrinkPhoto } from './OrderLines';

type Expense = { id: number; concept: string; category: string; category_label: string; amount: number; date: string; notes: string; photo: string | null };
type Profit = {
  from: string; to: string; orders: number; items: number; service_fees: number; delivery_fees: number; charged: number; expenses: number;
  profit: number; expenses_by_category: { category: string; label: string; amount: number }[];
  by_order: { uuid: string; folio: string; customer_name: string; date: string; items: number; service_fee: number; fee: number; amount: number }[];
};

const CATEGORIES = [
  ['fuel', 'Gasolina'], ['parking', 'Estacionamiento'], ['membership', 'Membresía'], ['shipping', 'Envíos'],
  ['supplies', 'Bolsas y empaque'], ['other', 'Otro'],
];
const PERIODS: { key: string; label: string; range: () => [string, string] }[] = [
  { key: 'month', label: 'Este mes', range: () => [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] },
  { key: 'last', label: 'Mes pasado', range: () => {
    const last = dayjs().subtract(1, 'month');
    return [last.startOf('month').format('YYYY-MM-DD'), last.endOf('month').format('YYYY-MM-DD')];
  } },
  { key: 'week', label: 'Últimos 7 días', range: () => [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] },
];

/**
 * "Ganancia" in Pedidos: for a business that buys what its customers ask for, what it earned in the period (service and
 * delivery fees of the charged orders) minus what it spent to do it (gas, parking, the membership…), and those expenses.
 */
const OrdersProfit = () => {
  const axios = useAxios();
  const [period, setPeriod] = React.useState('month');
  const [[from, to], setRange] = React.useState<[string, string]>(PERIODS[0].range());
  const [profit, setProfit] = React.useState<Profit | null>(null);
  const [expenses, setExpenses] = React.useState<Expense[]>([]);
  const [draft, setDraft] = React.useState({ concept: '', amount: null as number | null, category: 'fuel', date: dayjs().format('YYYY-MM-DD') });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const photoFor = React.useRef<number | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(() => {
    const query = `?from=${from}&to=${to}`;
    axios.get(`api/orders/profit/${query}`).then(r => setProfit(r.data)).catch(() => setProfit(null));
    axios.get(`api/expenses/${query}`).then(r => setExpenses(r.data ?? [])).catch(() => setExpenses([]));
  }, [axios, from, to]);
  React.useEffect(load, [load]);

  const choose = (key: string) => {
    setPeriod(key);
    const preset = PERIODS.find(p => p.key === key);
    if (preset) setRange(preset.range());
  };
  const add = async () => {
    if (!draft.concept.trim() || !draft.amount) { setError('Escribe el concepto y el monto.'); return; }
    setBusy(true);
    setError('');
    try {
      await axios.post('api/expenses/', draft);
      setDraft(d => ({ ...d, concept: '', amount: null }));
      load();
    } catch (err) {
      setError(errorText(err, 'No se pudo guardar el gasto.'));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (expense: Expense) => {
    if (!window.confirm(`¿Borrar el gasto «${expense.concept}» de ${currency(expense.amount)}?`)) return;
    try {
      await axios.delete(`api/expenses/${expense.id}/`);
      load();
    } catch (err) {
      setError(errorText(err, 'No se pudo borrar el gasto.'));
    }
  };
  const upload = async (file: File | undefined) => {
    const id = photoFor.current;
    if (!file || id == null) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', await shrinkPhoto(file), 'ticket.jpg');
      await axios.post(`api/expenses/${id}/photo/`, form);
      load();
    } catch (err) {
      setError(errorText(err, 'No se pudo subir la foto.'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' } }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {PERIODS.map(p => <Chip key={p.key} label={p.label} color={period === p.key ? 'primary' : 'default'} onClick={() => choose(p.key)} />)}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField size="small" type="date" label="Desde" value={from} onChange={e => { setPeriod(''); setRange([e.target.value, to]); }}
            slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" type="date" label="Hasta" value={to} onChange={e => { setPeriod(''); setRange([from, e.target.value]); }}
            slotProps={{ inputLabel: { shrink: true } }} />
        </Stack>
      </Stack>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}><StatCard label="Pedidos cobrados" value={profit?.orders ?? 0} loading={!profit} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><StatCard label="Comisiones y envíos" value={currency((profit?.service_fees ?? 0) + (profit?.delivery_fees ?? 0))} loading={!profit} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><StatCard label="Gastos" value={currency(profit?.expenses ?? 0)} loading={!profit} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Box data-testid="profit"><StatCard label="Ganancia" value={currency(profit?.profit ?? 0)} loading={!profit} /></Box>
        </Grid>
      </Grid>
      {profit && profit.orders > 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Tus clientes pagaron {currency(profit.charged)}: {currency(profit.items)} de productos (lo que costaron) y {currency(profit.service_fees + profit.delivery_fees)} de comisiones y envíos.
        </Typography>
      )}

      <SectionCard title="Gastos" subtitle="Gasolina, estacionamiento, la membresía… Lo que pagas para surtir los pedidos; no se le cobra a tus clientes.">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField size="small" label="Concepto" value={draft.concept} onChange={e => setDraft(d => ({ ...d, concept: e.target.value }))}
            sx={{ flex: 2 }} slotProps={{ htmlInput: { maxLength: 120, 'data-testid': 'expense-concept' } }} />
          <NumberField size="small" label="Monto" value={draft.amount} onChange={v => setDraft(d => ({ ...d, amount: v }))} sx={{ flex: 1 }}
            slotProps={{ htmlInput: { 'data-testid': 'expense-amount' } }} />
          <TextField size="small" select label="Tipo" value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))} sx={{ flex: 1, minWidth: 150 }}>
            {CATEGORIES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="Fecha" value={draft.date} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))}
            slotProps={{ inputLabel: { shrink: true } }} />
          <Button variant="contained" onClick={add} disabled={busy} data-testid="add-expense">Agregar</Button>
        </Stack>
        {expenses.length === 0 ? (
          <EmptyState compact icon={<ReceiptLongOutlinedIcon />} title="Sin gastos en este periodo" />
        ) : (
          <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
            {expenses.map(expense => (
              <Stack key={expense.id} direction="row" spacing={1.5} data-testid="expense" sx={{ py: 1, alignItems: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{expense.concept}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{expense.category_label} · {dayjs(expense.date).format('D MMM YYYY')}</Typography>
                </Box>
                {expense.photo
                  ? <Box component="a" href={expense.photo} target="_blank" rel="noopener noreferrer"><Box component="img" src={expense.photo} alt="Ticket" sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} /></Box>
                  : (
                    <Tooltip title="Foto del ticket">
                      <IconButton size="small" aria-label="Foto del ticket" onClick={() => { photoFor.current = expense.id; fileRef.current?.click(); }}>
                        <AddPhotoAlternateOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{currency(expense.amount)}</Typography>
                <IconButton size="small" aria-label={`Borrar ${expense.concept}`} onClick={() => remove(expense)}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
          </Stack>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => upload(e.target.files?.[0])} />
      </SectionCard>

      {profit && profit.by_order.length > 0 && (
        <SectionCard title="Pedidos cobrados" subtitle="Lo que ganaste en cada uno.">
          <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
            {profit.by_order.map(o => (
              <Stack key={o.uuid} direction="row" spacing={1.5} sx={{ py: 1, alignItems: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{o.customer_name || 'Cliente'} · {o.folio}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{dayjs(o.date).format('D MMM')} · productos {currency(o.items)} · total {currency(o.amount)}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>+{currency(o.service_fee + o.fee)}</Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}
    </Stack>
  );
};

export default OrdersProfit;
